import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type PrescriptionRecord } from '../../api';
import type { MarketplaceProvider, ProviderPricing, ProviderCertificate } from '../../types';
import { onPharmacyOrderConfirmed, formatPrice } from './bookingUtils';
import { useStore, store } from '../../state/patientProductionStore';
import { pharmacyPublicApi, PharmacyPublicApiError, rememberPharmacyOrder } from '../../pharmacyPublicApi';
import type { PublicStoreDetail } from '../../pharmacyPublicApi';

type PaymentMethodType = 'UPI' | 'CARD' | 'NET_BANKING' | 'COD' | 'WALLET' | 'INSURANCE';

// Only pay-on-delivery/pickup is offered: there is no online payment gateway
// yet, so listing UPI/Card/Wallet here would fake a charge that never happens.
const PAYMENT_METHODS: { key: PaymentMethodType; label: string; icon: string; desc: string }[] = [
  { key: 'COD', label: 'Pay on Delivery / Pickup', icon: '💵', desc: 'Cash or UPI when you receive the order · Online payment coming soon' },
];

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconBack({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconPhone({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27l3-.01a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9a16 16 0 0 0 6.29 6.29l1.48-1.48a2 2 0 0 1 2.09-.44c.91.35 1.87.59 2.84.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function IconClock({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconPlus({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5v14" /><path d="M5 12h14" />
    </svg>
  );
}

function IconMinus({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
      <path d="M5 12h14" />
    </svg>
  );
}

function IconTrash({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
  );
}

function IconCheck({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconCart({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}

function IconUpload({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

// ─── Demo Medicines ──────────────────────────────────────────────────────────

interface DemoMedicine {
  id: string;
  name: string;
  category: string;
  form: string;
  strength: string;
  price: number;
  requiresRx: boolean;
  inStock: boolean;
}


// ─── Cart item type ──────────────────────────────────────────────────────────

interface CartItem {
  id: string;
  name: string;
  qty: number;
  price: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parsePricing(raw: ProviderPricing[] | string | undefined): ProviderPricing[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return raw;
}

function parseCertificates(raw: ProviderCertificate[] | string | undefined): ProviderCertificate[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return []; }
  }
  return raw;
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function PharmacyStore() {
  const navigate = useNavigate();
  const { pharmacyId } = useParams<{ pharmacyId: string }>();
  const [searchParams] = useSearchParams();
  const prescriptionId = searchParams.get('prescriptionId');
  const medicineQuery = searchParams.get('medicine');
  // Real signed prescription from the backend (local store never has any).
  const [linkedPrescription, setLinkedPrescription] = useState<PrescriptionRecord | null>(null);
  useEffect(() => {
    let alive = true;
    if (!prescriptionId) { setLinkedPrescription(null); return; }
    patientApi.getPrescription(prescriptionId)
      .then((rx) => { if (alive) setLinkedPrescription(rx); })
      .catch(() => { if (alive) setLinkedPrescription(null); });
    return () => { alive = false; };
  }, [prescriptionId]);

  const [provider, setProvider] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real online storefront (catalogue + prices from the pharmacy's Medicine
  // Master). `storeUnavailable` = this pharmacy hasn't enabled online ordering.
  const [storeDetail, setStoreDetail] = useState<PublicStoreDetail | null>(null);
  const [storeUnavailable, setStoreUnavailable] = useState(false);
  const [storeLoading, setStoreLoading] = useState(true);

  // Medicine browsing state
  const [medSearch, setMedSearch] = useState(medicineQuery || '');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Cart state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [rxLoaded, setRxLoaded] = useState(false);

  // Order form state
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<1 | 2 | 3>(1);
  const [deliveryMode, setDeliveryMode] = useState<'HOME' | 'PICKUP'>('HOME');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('COD');
  const [consent, setConsent] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Buyer contact — the public order is placed with name + phone (that phone
  // is also the proof used to track the order later). Prefilled from the
  // patient profile, editable before placing.
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerNhcId, setBuyerNhcId] = useState('');
  useEffect(() => {
    let alive = true;
    patientApi.getProfile()
      .then((pr) => {
        if (!alive) return;
        setBuyerName((cur) => cur || pr.fullName || '');
        setBuyerPhone((cur) => cur || (pr.phone ?? '').replace(/\D/g, '').slice(-10));
        setBuyerNhcId((cur) => cur || pr.nhcId || '');
      })
      .catch(() => { /* fields stay editable-empty */ });
    return () => { alive = false; };
  }, []);

  // Success state
  const [orderResult, setOrderResult] = useState<{ refId: string; receiptId: string; itemCount: number; total: number; deliveryFee: number; paymentMethod: PaymentMethodType; prescriptionRequired: boolean } | null>(null);

  // Prescription upload placeholder
  const [showRxNote, setShowRxNote] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!pharmacyId) return;
    patientApi
      .getMarketplaceProvider(pharmacyId)
      .then((data) => { if (alive) setProvider(data); })
      .catch(() => { if (alive) setError('Unable to load pharmacy details.'); })
      .finally(() => { if (alive) setLoading(false); });
    pharmacyPublicApi
      .getStore(pharmacyId)
      .then((detail) => { if (alive) { setStoreDetail(detail); setStoreUnavailable(false); } })
      .catch(() => { if (alive) setStoreUnavailable(true); })
      .finally(() => { if (alive) setStoreLoading(false); });
    return () => { alive = false; };
  }, [pharmacyId]);

  // Auto-load prescription medicines into the cart by matching them against
  // this store's REAL catalogue. Only matched items are orderable (the public
  // order endpoint takes medicineIds); unmatched ones are listed honestly.
  const [unmatchedRxMeds, setUnmatchedRxMeds] = useState<string[]>([]);
  useEffect(() => {
    if (!linkedPrescription || rxLoaded || storeLoading) return;
    const catalogue = storeDetail?.medicines ?? [];
    const matchOne = (rxName: string) => {
      const n = rxName.trim().toLowerCase();
      if (!n) return undefined;
      return catalogue.find((m) => {
        const hay = [m.medicineName, m.genericName, m.brand].filter(Boolean).map((x) => String(x).toLowerCase());
        return hay.some((h) => h.includes(n) || n.includes(h));
      });
    };
    const matchedCart: CartItem[] = [];
    const unmatched: string[] = [];
    for (const med of linkedPrescription.medicines) {
      const rxName = String((med as any).medicineName ?? (med as any).name ?? (med as any).genericName ?? '');
      const hit = matchOne(rxName);
      if (hit && hit.inStock) {
        if (!matchedCart.some((c) => c.id === hit.id)) {
          matchedCart.push({ id: hit.id, name: hit.medicineName, qty: 1, price: hit.sellingPrice });
        }
      } else if (rxName) {
        unmatched.push(rxName);
      }
    }
    setCart(matchedCart);
    setUnmatchedRxMeds(unmatched);
    setRxLoaded(true);
  }, [linkedPrescription, rxLoaded, storeDetail, storeLoading]);

  // REAL catalogue rows — the pharmacy's Medicine Master (public + online-sale
  // flagged), with its actual selling prices and stock. Nothing invented.
  const allMedicines: DemoMedicine[] = useMemo(() => (storeDetail?.medicines ?? []).map((m) => ({
    id: m.id,
    name: [m.medicineName, m.brand && m.brand !== m.medicineName ? `(${m.brand})` : ''].filter(Boolean).join(' '),
    category: m.dosageForm || 'Other',
    form: m.dosageForm || '—',
    strength: m.strength || '',
    price: m.sellingPrice,
    requiresRx: m.prescriptionRequired,
    inStock: m.inStock,
  })), [storeDetail]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(allMedicines.map((m) => m.category))).sort()], [allMedicines]);

  const filteredMedicines: DemoMedicine[] = useMemo(() => {
    const q = medSearch.trim().toLowerCase();
    return allMedicines.filter((m) => {
      if (selectedCategory !== 'All' && m.category !== selectedCategory) return false;
      if (q && !(`${m.name} ${m.strength}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allMedicines, medSearch, selectedCategory]);

  // Cart helpers
  const cartTotal = useMemo(() => cart.reduce((sum, c) => sum + c.price * c.qty, 0), [cart]);
  const cartItemCount = useMemo(() => cart.reduce((sum, c) => sum + c.qty, 0), [cart]);

  const addToCart = (med: DemoMedicine) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.id === med.id);
      if (existing) {
        return prev.map((c) => c.id === med.id ? { ...c, qty: c.qty + 1 } : c);
      }
      return [...prev, { id: med.id, name: med.name, qty: 1, price: med.price }];
    });
  };

  const updateCartQty = (id: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((c) => c.id === id ? { ...c, qty: Math.max(1, c.qty + delta) } : c)
        .filter((c) => c.qty >= 1)
    );
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((c) => c.id !== id));
  };

  const getCartQty = (id: string): number => {
    const item = cart.find((c) => c.id === id);
    return item ? item.qty : 0;
  };

  // No delivery fee is charged by the online order — any delivery charge is
  // confirmed by the pharmacy at fulfilment (the old ₹49/FREE tiers were fiction).
  const deliveryFee = 0;
  const orderTotal = cartTotal;

  // Place order — a REAL PharmacySaleOrder on the pharmacy's online-store
  // queue (the earlier marketplace booking never reached the pharmacy panel).
  const handlePlaceOrder = async () => {
    if (!provider || !pharmacyId) return;
    if (storeUnavailable) {
      setOrderError('This pharmacy has not enabled online ordering.');
      return;
    }
    if (cart.length === 0) {
      setOrderError('Your cart is empty. Add medicines first.');
      return;
    }
    if (!buyerName.trim() || buyerPhone.replace(/\D/g, '').length < 10) {
      setOrderError('Your name and a 10-digit phone number are required — the phone is also how you track this order.');
      return;
    }
    if (deliveryMode === 'HOME' && !deliveryAddress.trim()) {
      setOrderError('Please enter a delivery address.');
      return;
    }
    if (!consent) {
      setOrderError('Please accept the consent before placing the order.');
      return;
    }

    setOrdering(true);
    setOrderError(null);

    try {
      // The pharmacy backend order is the source of truth — its orderNumber is
      // the reference the user keeps, and its totalAmount includes real GST.
      // No payment is charged online (no gateway) — settled at pickup/delivery.
      const apiOrder = await pharmacyPublicApi.placeOrder(pharmacyId, {
        buyerName: buyerName.trim(),
        buyerPhone: buyerPhone.replace(/\D/g, ''),
        buyerNhcId: buyerNhcId || undefined,
        items: cart.map((c) => ({ medicineId: c.id, quantity: c.qty })),
        fulfillmentType: deliveryMode === 'HOME' ? 'DELIVERY' : 'PICKUP',
        deliveryAddress: deliveryMode === 'HOME' ? deliveryAddress.trim() : undefined,
        prescriptionId: prescriptionId || undefined,
      });

      // Persist tracking credentials — orderNumber + phone are how the Orders
      // page hydrates the live status later.
      rememberPharmacyOrder({
        orderNumber: apiOrder.orderNumber,
        phone: buyerPhone.replace(/\D/g, ''),
        slug: pharmacyId,
        pharmacyName: provider.name,
        placedAt: new Date().toISOString(),
      });

      // Store integration — local mirror for offline views, keyed by the
      // server orderNumber so orders pages can dedupe it against the API copy
      const order = onPharmacyOrderConfirmed({
        id: apiOrder.orderNumber,
        medicines: cart.map((c) => ({ name: c.name, qty: c.qty, dosage: '', unitPrice: c.price })),
        pharmacyName: provider.name,
        totalAmount: apiOrder.totalAmount,
        prescriptionId: prescriptionId || undefined,
        deliveryFee: 0,
        paymentMethod,
        paymentStatus: 'PENDING',
        deliveryMode,
        deliveryAddress: deliveryMode === 'HOME' ? deliveryAddress.trim() : undefined,
      });

      // Receipt is an order acknowledgement with the SERVER totals (incl GST)
      // — not proof of payment.
      const receipt = store.createPharmacyReceipt({
        orderId: order.id,
        pharmacyName: provider.name,
        medicines: cart.map(c => ({ name: c.name, qty: c.qty, unitPrice: c.price, subtotal: c.price * c.qty })),
        subtotal: cartTotal,
        deliveryFee: 0,
        discount: 0,
        tax: Math.max(0, apiOrder.totalAmount - cartTotal),
        total: apiOrder.totalAmount,
        paymentMethod,
        paymentStatus: 'PENDING',
      });

      setOrderResult({ refId: apiOrder.orderNumber, receiptId: receipt.id, itemCount: cartItemCount, total: apiOrder.totalAmount, deliveryFee: 0, paymentMethod, prescriptionRequired: apiOrder.prescriptionRequired });
    } catch (e) {
      if (e instanceof PharmacyPublicApiError) {
        setOrderError(e.fieldErrors?.length ? e.fieldErrors.map((f) => f.message).join(' · ') : e.message);
      } else {
        setOrderError('Order failed. Please try again.');
      }
    } finally {
      setOrdering(false);
    }
  };

  // ─── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 100 }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `3px solid ${T.border}`,
              borderTopColor: '#059669',
              margin: '0 auto 12px',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec }}>Loading pharmacy...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ─── Error ───────────────────────────────────────────────────────────────────

  if (error || !provider) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, paddingBottom: 100 }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: T.font, fontSize: 14, color: '#B91C1C', marginBottom: 16 }}>
            {error ?? 'Pharmacy not found.'}
          </p>
          <button
            onClick={() => navigate('/patient/care-services/pharmacy')}
            style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#059669', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer' }}
          >
            Back to Pharmacy
          </button>
        </div>
      </div>
    );
  }

  const pricing = parsePricing(provider.pricing);
  const certificates = parseCertificates(provider.certificates);

  // ─── Success screen ──────────────────────────────────────────────────────────

  if (orderResult) {
    const payInfo = PAYMENT_METHODS.find(p => p.key === orderResult.paymentMethod);
    return (
      <div style={{ background: T.bg, padding: '20px 16px', fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', background: '#05966915',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <IconCheck color="#059669" />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: '0 0 6px' }}>Order Placed!</h2>
          <p style={{ fontSize: 12, color: T.textSec, margin: 0 }}>
            Your order has been sent to <strong>{provider.name}</strong>.
          </p>
        </div>

        {/* Order summary card */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, boxShadow: T.shadow, marginBottom: 12 }}>
          <p style={{ fontSize: 10, color: T.textTer, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Order Reference</p>
          <p style={{ fontSize: 18, fontWeight: 800, color: '#059669', margin: '0 0 12px', letterSpacing: '0.06em' }}>{orderResult.refId}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              ['Pharmacy', provider.name],
              ['Items', `${orderResult.itemCount}`],
              ['Total (incl. GST)', `₹${formatPrice(orderResult.total)}`],
              ['Payment', `${payInfo?.icon || ''} ${payInfo?.label || orderResult.paymentMethod}`],
              ['Payment Status', deliveryMode === 'PICKUP' ? 'Pay at pickup' : 'Pay on delivery'],
              ['Delivery', deliveryMode === 'PICKUP' ? 'Pickup — pharmacy will confirm when ready' : 'Timing & any delivery charge confirmed by the pharmacy'],
            ].map(([label, value], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: i < 5 ? `1px solid ${T.border}20` : 'none' }}>
                <span style={{ fontSize: 12, color: T.textSec }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: label === 'Total (incl. GST)' ? 700 : 600, color: label === 'Total (incl. GST)' ? '#059669' : T.text }}>{value}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, margin: '10px 0 0' }}>
            Track this order any time with the order number above and the phone you used — see My Orders.
          </p>
        </div>

        {orderResult.prescriptionRequired && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 14, padding: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#92400E', fontFamily: T.font }}>Prescription review pending</span>
            </div>
            <p style={{ fontSize: 11, color: '#A16207', fontFamily: T.font, margin: 0 }}>
              This order contains Rx-required medicines — the pharmacist will verify the prescription before confirming. Carry the original prescription at pickup/delivery.
            </p>
          </div>
        )}

        {/* Receipt card */}
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 16 }}>🧾</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', fontFamily: T.font }}>Order Acknowledgement Saved</span>
          </div>
          <p style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, margin: '0 0 8px' }}>
            Acknowledgement #{orderResult.receiptId.slice(0, 8)} is saved on this device — the pharmacy issues the official invoice when it bills your order.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => navigate(`/patient/pharmacy/receipt/${orderResult.receiptId}`)}
              style={{ fontSize: 10, fontWeight: 600, fontFamily: T.font, color: '#059669', background: 'transparent', border: `1px solid #059669`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}
            >
              View Acknowledgement
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => navigate('/patient/pharmacy')}
            style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#059669', border: 'none', borderRadius: 12, padding: '12px 0', cursor: 'pointer', width: '100%' }}
          >
            View Orders
          </button>
          <button
            onClick={() => navigate('/patient/timeline')}
            style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: '#059669', background: '#05966910', border: '1.5px solid #05966930', borderRadius: 12, padding: '12px 0', cursor: 'pointer', width: '100%' }}
          >
            Go to Timeline
          </button>
        </div>

        {/* Provider confirmation note */}
        <div style={{ marginTop: 16, padding: '10px 14px', background: T.ice, borderRadius: 10, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
          <p style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, margin: 0, lineHeight: 1.5 }}>
            Provider confirmation pending — track live status anytime on the Orders page using your order number and phone.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
            NeoHealthCard™ · Order placed directly in the pharmacy's orderbook · Track with your order number + phone
          </span>
        </div>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────────────

  return (
    <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: cart.length > 0 ? 140 : 100 }}>
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, #059669 0%, #047857 100%)`,
          padding: '20px 16px 32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3h6l1 4H8L9 3z" /><rect x="4" y="7" width="16" height="14" rx="2" /><path d="M9 11h6" /><path d="M12 8v10" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: T.font, fontSize: 20, fontWeight: 800, color: T.white, margin: '0 0 4px', lineHeight: 1.3 }}>
              {provider.name}
            </h1>
            {provider.isVerified && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 700, color: T.white, fontFamily: T.font }}>
                ✓ NHC Verified
              </span>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
          {provider.rating > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
              <IconStar color="#FCD34D" /> {provider.rating.toFixed(1)} ({provider.reviewCount})
            </span>
          )}
          {provider.openHours && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
              <IconClock color="rgba(255,255,255,0.7)" /> {provider.openHours}
            </span>
          )}
        </div>
      </div>

      {/* Info card */}
      <div style={{ padding: '0 16px', marginTop: -16, position: 'relative', zIndex: 5 }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', boxShadow: T.shadow }}>
          {provider.address && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
              <IconLocation color={T.textTer} />
              <span style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, lineHeight: 1.5 }}>
                {provider.address}{provider.city ? `, ${provider.city}` : ''}{provider.state ? `, ${provider.state}` : ''}
              </span>
            </div>
          )}
          {provider.phone && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <IconPhone color={T.textTer} />
              <a href={`tel:${provider.phone}`} style={{ fontFamily: T.font, fontSize: 12, color: '#059669', textDecoration: 'none', fontWeight: 600 }}>
                {provider.phone}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Services / Pricing */}
      {pricing.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>
            Products & Pricing
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pricing.map((item, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 600, color: T.text, margin: '0 0 2px' }}>
                    {item.service}
                  </p>
                  {item.note && (
                    <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0 }}>{item.note}</p>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: '#059669' }}>
                    {formatPrice(item.price)}
                  </span>
                  {item.unit && (
                    <span style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, marginLeft: 2 }}>
                      /{item.unit}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {(provider.tags?.length ?? 0) > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 10px' }}>
            Services
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {provider.tags!.map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#059669',
                  background: '#05966912',
                  border: '1px solid #05966930',
                  borderRadius: 20,
                  padding: '5px 12px',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certificates */}
      {certificates.length > 0 && (
        <div style={{ padding: '20px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>
            Certificates & Accreditations
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {certificates.map((cert, i) => (
              <div
                key={i}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: '#05966912',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconShield color="#059669" />
                </div>
                <div>
                  <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 2px' }}>
                    {cert.name}
                  </p>
                  <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0 }}>
                    {cert.issuedBy} · {cert.year}
                    {cert.registrationNumber ? ` · Reg: ${cert.registrationNumber}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Prescription Context Banner ──────────────────────────────────── */}
      {linkedPrescription && (
        <div style={{ padding: '16px 16px 0' }}>
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>📋</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', fontFamily: T.font }}>Order from Prescription</span>
              <span style={{ fontSize: 8, fontWeight: 700, color: T.teal, background: `${T.teal}12`, borderRadius: 4, padding: '1px 6px', fontFamily: T.font }}>eRx</span>
            </div>
            <p style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, margin: '0 0 4px' }}>
              {linkedPrescription.doctorName}{linkedPrescription.clinicName ? ` · ${linkedPrescription.clinicName}` : ''}
            </p>
            <p style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, margin: 0 }}>
              {cart.length} of {linkedPrescription.medicines.length} prescription medicine{linkedPrescription.medicines.length !== 1 ? 's' : ''} matched in this store's catalogue and added to cart
            </p>
            {unmatchedRxMeds.length > 0 && (
              <div style={{ marginTop: 8, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 10px' }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#92400E', fontFamily: T.font, margin: '0 0 2px' }}>Not in this store's online catalogue</p>
                <p style={{ fontSize: 10, color: '#A16207', fontFamily: T.font, margin: 0 }}>
                  {unmatchedRxMeds.join(' · ')} — call the pharmacy to ask for these; they can't be added to this online order.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Prescription Upload Placeholder ────────────────────────────────── */}
      {!linkedPrescription && (
        <div style={{ padding: '20px 16px 0' }}>
          <button
            onClick={() => setShowRxNote(true)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              fontFamily: T.font, fontSize: 13, fontWeight: 700, color: '#7C3AED',
              background: '#F5F3FF', border: '1.5px dashed #7C3AED40', borderRadius: 12,
              padding: '12px 0', cursor: 'pointer',
            }}
          >
            <IconUpload color="#7C3AED" /> Upload Prescription
          </button>
          {showRxNote && (
            <div style={{ marginTop: 8, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>&#9888;</span>
              <div>
                <p style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: '#92400E', margin: '0 0 2px' }}>Prescription file upload is not available yet</p>
                <p style={{ fontFamily: T.font, fontSize: 11, color: '#A16207', margin: 0 }}>
                  Agar aapke doctor ne NeoHealthCard par prescription sign kiya hai, to Prescriptions page se "Order Medicines" use karo — medicines is store ke catalogue se auto-match ho jaati hain. Warna neeche list se manually add karo; Rx-required items pharmacist billing se pehle prescription verify karta hai.
                </p>
              </div>
              <button onClick={() => setShowRxNote(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#A16207', fontSize: 14, padding: 0, flexShrink: 0 }}>x</button>
            </div>
          )}
        </div>
      )}

      {/* ─── Medicine Browsing Section ──────────────────────────────────────── */}
      <div style={{ padding: '20px 16px 0' }}>
        <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>
          Browse Medicines
        </h2>

        {storeLoading && (
          <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 12px' }}>Loading this pharmacy's live catalogue…</p>
        )}
        {!storeLoading && storeUnavailable && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
            <p style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#92400E', margin: '0 0 3px' }}>Online ordering isn't enabled for this pharmacy yet</p>
            <p style={{ fontFamily: T.font, fontSize: 11, color: '#A16207', margin: 0 }}>
              No live catalogue is published, so medicines can't be ordered from here.
              {provider.phone ? ` Call the pharmacy at ${provider.phone} instead.` : ' Contact the pharmacy directly instead.'}
            </p>
          </div>
        )}

        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            padding: '9px 12px',
            marginBottom: 12,
          }}
        >
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search medicines..."
            value={medSearch}
            onChange={(e) => setMedSearch(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {medSearch && (
            <button
              onClick={() => setMedSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textTer, fontSize: 16, padding: 0 }}
            >
              x
            </button>
          )}
        </div>

        {/* Category chips */}
        <div style={{ overflowX: 'auto', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 6, width: 'max-content' }}>
            {categories.map((cat) => {
              const active = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    fontFamily: T.font,
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? T.white : T.textSec,
                    background: active ? '#059669' : T.white,
                    border: `1px solid ${active ? '#059669' : T.border}`,
                    borderRadius: 20,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Medicine cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filteredMedicines.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: 0 }}>
                {storeUnavailable
                  ? 'No online catalogue for this pharmacy.'
                  : medSearch || selectedCategory !== 'All'
                    ? `No medicines match${medSearch ? ` "${medSearch}"` : ''}.`
                    : storeLoading ? 'Loading…' : 'This store hasn\'t published any medicines online yet.'}
              </p>
            </div>
          ) : (
            filteredMedicines.map((med) => {
              const inCart = getCartQty(med.id);
              return (
                <div
                  key={med.id}
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    padding: '12px 14px',
                    boxShadow: T.shadow,
                    opacity: med.inStock ? 1 : 0.6,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                        <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>
                          {med.name}
                        </span>
                        {med.requiresRx && (
                          <span style={{
                            fontFamily: T.font, fontSize: 9, fontWeight: 700,
                            color: '#B91C1C', background: '#FEF2F2',
                            border: '1px solid #FCA5A5', borderRadius: 4,
                            padding: '1px 5px',
                          }}>
                            Rx Required
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                          {med.form} · {med.strength}
                        </span>
                        <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                          {med.category}
                        </span>
                      </div>
                    </div>
                    <span style={{ fontFamily: T.font, fontSize: 15, fontWeight: 700, color: '#059669', flexShrink: 0 }}>
                      {formatPrice(med.price)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    {/* Stock status */}
                    <span style={{
                      fontFamily: T.font, fontSize: 10, fontWeight: 600,
                      color: med.inStock ? '#059669' : '#B91C1C',
                    }}>
                      {med.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>

                    {/* Add to cart / qty controls */}
                    {med.inStock && (
                      inCart > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <button
                            onClick={() => inCart === 1 ? removeFromCart(med.id) : updateCartQty(med.id, -1)}
                            style={{
                              width: 30, height: 30,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: '#FEF2F2', border: '1px solid #FCA5A5',
                              borderRadius: '8px 0 0 8px', cursor: 'pointer', padding: 0,
                            }}
                          >
                            {inCart === 1 ? <IconTrash color="#EF4444" /> : <IconMinus color="#EF4444" />}
                          </button>
                          <div
                            style={{
                              width: 34, height: 30,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: T.white, border: `1px solid ${T.border}`,
                              borderLeft: 'none', borderRight: 'none',
                              fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text,
                            }}
                          >
                            {inCart}
                          </div>
                          <button
                            onClick={() => updateCartQty(med.id, 1)}
                            style={{
                              width: 30, height: 30,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: '#05966912', border: '1px solid #05966930',
                              borderRadius: '0 8px 8px 0', cursor: 'pointer', padding: 0,
                            }}
                          >
                            <IconPlus color="#059669" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addToCart(med)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontFamily: T.font, fontSize: 11, fontWeight: 700,
                            color: '#059669', background: '#05966910',
                            border: '1.5px solid #05966930', borderRadius: 8,
                            padding: '6px 12px', cursor: 'pointer',
                          }}
                        >
                          <IconPlus color="#059669" /> Add
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Inline Order Form ──────────────────────────────────────────────── */}
      {showOrderForm && (
        <div style={{ padding: '20px 16px 0' }}>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px', boxShadow: T.shadow }}>
            <h3 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
              Complete Your Order
            </h3>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: '0 0 14px' }}>
              {cart.length} item{cart.length !== 1 ? 's' : ''} · Total: {formatPrice(cartTotal)}
            </p>

            {/* Cart summary */}
            <div style={{ marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
              {cart.map((item) => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div>
                    <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text }}>{item.name}</span>
                    <span style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginLeft: 6 }}>x{item.qty}</span>
                  </div>
                  <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text }}>{formatPrice(item.price * item.qty)}</span>
                </div>
              ))}
            </div>

            {/* Delivery mode */}
            <h4 style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 8px' }}>
              Delivery Mode
            </h4>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {(['HOME', 'PICKUP'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setDeliveryMode(mode)}
                  style={{
                    flex: 1,
                    fontFamily: T.font,
                    fontSize: 12,
                    fontWeight: 600,
                    color: deliveryMode === mode ? T.white : T.textSec,
                    background: deliveryMode === mode ? '#059669' : T.white,
                    border: `1.5px solid ${deliveryMode === mode ? '#059669' : T.border}`,
                    borderRadius: 10,
                    padding: '9px 0',
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {mode === 'HOME' ? 'Home Delivery' : 'Pickup'}
                </button>
              ))}
            </div>

            {/* Buyer contact — phone doubles as the order-tracking proof */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                Your Name
              </label>
              <input
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
                placeholder="Full name"
                style={{ width: '100%', fontFamily: T.font, fontSize: 13, color: T.text, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                Phone Number
              </label>
              <input
                value={buyerPhone}
                onChange={(e) => setBuyerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                style={{ width: '100%', fontFamily: T.font, fontSize: 13, color: T.text, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', outline: 'none', boxSizing: 'border-box' }}
              />
              <p style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, margin: '4px 0 0' }}>
                You'll track this order with the order number + this phone.
              </p>
            </div>

            {/* Delivery address */}
            {deliveryMode === 'HOME' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.text, display: 'block', marginBottom: 6 }}>
                  Delivery Address
                </label>
                <textarea
                  rows={3}
                  placeholder="Enter your full delivery address..."
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  style={{
                    width: '100%',
                    fontFamily: T.font,
                    fontSize: 13,
                    color: T.text,
                    background: T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {/* Payment method */}
            <h4 style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 8px' }}>
              Payment Method
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {PAYMENT_METHODS.map((pm) => {
                const active = paymentMethod === pm.key;
                return (
                  <button
                    key={pm.key}
                    onClick={() => setPaymentMethod(pm.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      fontFamily: T.font, textAlign: 'left',
                      padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                      background: active ? '#05966908' : T.white,
                      border: `1.5px solid ${active ? '#059669' : T.border}`,
                      transition: 'border-color 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{pm.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{pm.label}</div>
                      <div style={{ fontSize: 10, color: T.textTer }}>{pm.desc}</div>
                    </div>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${active ? '#059669' : T.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {active && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669' }} />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Order total breakdown */}
            <div style={{ background: T.ice, borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: T.textSec, fontFamily: T.font }}>Subtotal</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>₹{formatPrice(cartTotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: T.textSec, fontFamily: T.font }}>GST</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>Added on order confirmation</span>
              </div>
              {deliveryMode === 'HOME' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: T.textSec, fontFamily: T.font }}>Delivery Fee</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font }}>Confirmed by pharmacy</span>
                </div>
              )}
              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 6, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>Items total</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#059669', fontFamily: T.font }}>₹{formatPrice(orderTotal)}</span>
              </div>
            </div>

            {/* Consent */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                marginBottom: 14,
                background: T.ice ?? '#F0F9FF',
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: '10px 12px',
              }}
            >
              <button
                onClick={() => setConsent(!consent)}
                style={{
                  width: 20, height: 20, flexShrink: 0,
                  borderRadius: 5,
                  border: `1.5px solid ${consent ? '#059669' : T.border}`,
                  background: consent ? '#059669' : T.white,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0, marginTop: 1,
                }}
              >
                {consent && (
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
              <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0, lineHeight: 1.6 }}>
                I consent to sharing my order and prescription details with this pharmacy per NeoHealthCard policy.
              </p>
            </div>

            {/* Order error */}
            {orderError && (
              <div
                style={{
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 12,
                }}
              >
                <p style={{ fontFamily: T.font, fontSize: 12, color: '#B91C1C', margin: 0 }}>{orderError}</p>
              </div>
            )}

            {/* Confirm button */}
            <button
              onClick={handlePlaceOrder}
              disabled={ordering}
              style={{
                width: '100%',
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.white,
                background: ordering ? '#6EE7B7' : '#059669',
                border: 'none',
                borderRadius: 12,
                padding: '14px 0',
                cursor: ordering ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {ordering ? 'Placing Order...' : `Confirm Order · ₹${formatPrice(orderTotal)}`}
            </button>

            {/* Cancel */}
            <button
              onClick={() => { setShowOrderForm(false); setOrderError(null); }}
              style={{
                width: '100%',
                fontFamily: T.font,
                fontSize: 12,
                fontWeight: 600,
                color: T.textSec,
                background: 'transparent',
                border: 'none',
                padding: '10px 0 0',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ─── Floating Cart Bar ──────────────────────────────────────────────── */}
      {cart.length > 0 && !showOrderForm && (
        <div
          style={{
            position: 'fixed',
            bottom: 70,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 1080,
            background: T.white,
            borderTop: `1px solid ${T.border}`,
            padding: '12px 16px',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconCart color="#059669" />
            <div>
              <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text }}>
                {cartItemCount} item{cartItemCount !== 1 ? 's' : ''}
              </span>
              <span style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: '#059669', marginLeft: 8 }}>
                {formatPrice(cartTotal)}
              </span>
            </div>
          </div>
          <button
            onClick={() => setShowOrderForm(true)}
            style={{
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 700,
              color: T.white,
              background: '#059669',
              border: 'none',
              borderRadius: 10,
              padding: '10px 20px',
              cursor: 'pointer',
            }}
          >
            Place Order
          </button>
        </div>
      )}
    </div>
  );
}
